import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Slide,
  Switch,
  Typography,
} from '@mui/material';
import type { TransitionProps } from '@mui/material/transitions';
import { useI18n } from '../contexts/I18nProvider';
import {
  acceptAll,
  ConsentState,
  getConsent,
  rejectOptional,
  setConsent,
} from '../lib/cookies/consent';
import { loadGA4, loadGTM, loadMetaPixel } from '../lib/cookies/scripts';

const SlideUp = React.forwardRef(function SlideUp(
  props: TransitionProps & { children: React.ReactElement },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const GTM_ID = process.env.REACT_APP_GTM_ID || '';
const GA4_ID = process.env.REACT_APP_GA4_ID || '';
const META_PIXEL_ID = process.env.REACT_APP_META_PIXEL_ID || '';

function applyConsent(state: ConsentState) {
  if (state.analytics) {
    if (GTM_ID) loadGTM(GTM_ID);
    if (GA4_ID) loadGA4(GA4_ID);
  }
  if (state.marketing) {
    if (META_PIXEL_ID) loadMetaPixel(META_PIXEL_ID);
  }
}

const CookieConsent: React.FC = () => {
  const { t } = useI18n();
  const [bannerOpen, setBannerOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);

  // On mount: read cookie, show banner if no consent, load scripts if already consented
  // Skip during ReactSnap prerender to avoid hydration mismatch (error #418)
  useEffect(() => {
    if (navigator.userAgent === 'ReactSnap') return;
    const saved = getConsent();
    if (!saved) {
      setBannerOpen(true);
    } else {
      setAnalytics(saved.analytics);
      setMarketing(saved.marketing);
      applyConsent(saved);
    }
  }, []);

  // Listen for footer "open-cookie-preferences" event
  useEffect(() => {
    const handler = () => {
      const saved = getConsent();
      if (saved) {
        setAnalytics(saved.analytics);
        setMarketing(saved.marketing);
      }
      setDialogOpen(true);
    };
    window.addEventListener('open-cookie-preferences', handler);
    return () => window.removeEventListener('open-cookie-preferences', handler);
  }, []);

  const handleAcceptAll = useCallback(() => {
    const state = acceptAll();
    setAnalytics(true);
    setMarketing(true);
    applyConsent(state);
    setBannerOpen(false);
  }, []);

  const handleReject = useCallback(() => {
    rejectOptional();
    setAnalytics(false);
    setMarketing(false);
    setBannerOpen(false);
  }, []);

  const handleOpenPreferences = useCallback(() => {
    setBannerOpen(false);
    setDialogOpen(true);
  }, []);

  const handleSavePreferences = useCallback(() => {
    const state: ConsentState = {
      necessary: true,
      analytics,
      marketing,
      updatedAt: new Date().toISOString(),
    };
    setConsent(state);
    applyConsent(state);
    setDialogOpen(false);
    setBannerOpen(false);
  }, [analytics, marketing]);

  return (
    <>
      {/* Banner */}
      <Slide direction="up" in={bannerOpen} mountOnEnter unmountOnExit>
        <Box
          sx={{
            position: 'fixed',
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 1400,
            bgcolor: '#1F1F1F',
            color: '#fff',
            px: { xs: 2, sm: 4 },
            py: 2,
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: { xs: 'stretch', md: 'center' },
            gap: 2,
            boxShadow: '0 -2px 16px rgba(0,0,0,0.2)',
          }}
        >
          <Typography sx={{ flex: 1, fontSize: '0.875rem', lineHeight: 1.5 }}>
            {t('cookies.banner_text')}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, flexShrink: 0, flexWrap: 'wrap' }}>
            <Button
              variant="outlined"
              size="small"
              onClick={handleReject}
              sx={{
                color: '#fff',
                borderColor: 'rgba(255,255,255,0.4)',
                textTransform: 'none',
                '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.1)' },
              }}
            >
              {t('cookies.reject')}
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={handleOpenPreferences}
              sx={{
                color: '#fff',
                borderColor: 'rgba(255,255,255,0.4)',
                textTransform: 'none',
                '&:hover': { borderColor: '#fff', bgcolor: 'rgba(255,255,255,0.1)' },
              }}
            >
              {t('cookies.preferences')}
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={handleAcceptAll}
              sx={{
                bgcolor: '#e72f0d',
                textTransform: 'none',
                '&:hover': { bgcolor: '#c4260b' },
              }}
            >
              {t('cookies.accept_all')}
            </Button>
          </Box>
        </Box>
      </Slide>

      {/* Preferences Dialog */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        TransitionComponent={SlideUp}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{t('cookies.preferences_title')}</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 3 }}>
            {t('cookies.preferences_desc')}
          </Typography>

          {/* Necessary */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box>
              <Typography variant="subtitle2">{t('cookies.necessary_title')}</Typography>
              <Typography variant="caption" color="text.secondary">
                {t('cookies.necessary_desc')}
              </Typography>
            </Box>
            <Switch checked disabled />
          </Box>

          {/* Analytics */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box>
              <Typography variant="subtitle2">{t('cookies.analytics_title')}</Typography>
              <Typography variant="caption" color="text.secondary">
                {t('cookies.analytics_desc')}
              </Typography>
            </Box>
            <Switch checked={analytics} onChange={(_, v) => setAnalytics(v)} />
          </Box>

          {/* Marketing */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box>
              <Typography variant="subtitle2">{t('cookies.marketing_title')}</Typography>
              <Typography variant="caption" color="text.secondary">
                {t('cookies.marketing_desc')}
              </Typography>
            </Box>
            <Switch checked={marketing} onChange={(_, v) => setMarketing(v)} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} sx={{ textTransform: 'none' }}>
            {t('cookies.cancel')}
          </Button>
          <Button variant="contained" onClick={handleSavePreferences} sx={{ textTransform: 'none' }}>
            {t('cookies.save')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CookieConsent;
